import React, { useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import DashboardFilterDropdown from './DashboardFilter'; // Adjust path if needed
import { DashboardFilter } from '../../../api/dashbboards/DashboardFilter';
import { OptionsBuilderItemTypes } from '../../../types/dataframes/options-builder-item-types';
import { TDimension } from '../../../api/analytics/Dimension';
import { useCommonStoreContext } from '../../../components/common-store/CommonStoreProvider';

type Props = {
	dashboardFilters: DashboardFilter[];
	allSeries: TDimension[];
	isView: boolean;
	allowEdit: (id: number) => boolean;
	handleFilterModalOpen: (dashboardFilterId: number, entityId: number) => void;
	handleRemoveDashboardFilter: (id: number) => void;
	setDashboardFilters: (filters: DashboardFilter[]) => void;
	setDashboardFiltersTemp?: React.Dispatch<React.SetStateAction<DashboardFilter[] | undefined>>;
};

const DashboardFiltersHorizontalGrid: React.FC<Props> = ({
	dashboardFilters,
	allSeries,
	isView,
	allowEdit,
	handleFilterModalOpen,
	handleRemoveDashboardFilter,
	setDashboardFilters,
	setDashboardFiltersTemp,
}) => {
	const { setCurrentFilterName } = useCommonStoreContext();

	const getDashboardFilter = useCallback(
		(dashboardFilter: DashboardFilter) => {
			let name = '';
			const current = dashboardFilters.find((df) => df.id === dashboardFilter.id);

			if (dashboardFilter.entity_type === OptionsBuilderItemTypes.Dimension) {
				const dim = allSeries.find((s) => s.id === dashboardFilter.entity_id);
				name = dim?.name ?? '';
			} else if (dashboardFilter.entity_type === OptionsBuilderItemTypes.DimensionAttribute) {
				const dim = allSeries.find((s) =>
					s.dimensionAttributes.some(
						(attr) =>
							attr.id === dashboardFilter.entity_id &&
							attr.type === dashboardFilter.entity_type
					)
				);

				const dimAttr = dim?.dimensionAttributes.find(
					(attr) =>
						attr.id === dashboardFilter.entity_id &&
						attr.type === dashboardFilter.entity_type
				);
				name = dimAttr?.name ?? '';
			}
			setCurrentFilterName(name);

			return (
				<DashboardFilterDropdown
					id={dashboardFilter.id ?? 0}
					entityId={dashboardFilter.entity_id}
					handleFilterModalOpen={handleFilterModalOpen}
					handleRemoveDashboardFilter={handleRemoveDashboardFilter}
					name={name}
					isView={isView}
					editable={allowEdit(dashboardFilter.id ?? 0)}
					isFiltered={current?.value && current.value !== '' ? true : false}
				/>
			);
		},
		[
			dashboardFilters,
			allSeries,
			isView,
			allowEdit,
			handleFilterModalOpen,
			handleRemoveDashboardFilter,
		]
	);

	const onDragEnd = (result: DropResult) => {
		const { source, destination } = result;

		if (!destination || source.index === destination.index) return;

		const reordered = Array.from(dashboardFilters);
		const [moved] = reordered.splice(source.index, 1);
		reordered.splice(destination.index, 0, moved);

		//Update sequence for all items
		const withUpdatedSequence = reordered.map((item, index) => ({
			...item,
			sequence: index,
		}));

		setDashboardFilters(withUpdatedSequence);
		setDashboardFiltersTemp &&
			setDashboardFiltersTemp(withUpdatedSequence as DashboardFilter[]);
	};

	return (
		<DragDropContext onDragEnd={onDragEnd}>
			<Droppable
				droppableId="horizontal-dnd"
				direction="horizontal"
			>
				{(provided) => (
					<div
						ref={provided.innerRef}
						{...provided.droppableProps}
						style={{
							display: 'flex',
							gap: '8px',
							overflowX: 'auto',
						}}
					>
						{dashboardFilters.map((df, index) => (
							<Draggable
								draggableId={df.id?.toString() ?? `item-${df.entity_id}`}
								index={index}
								key={df.id?.toString() ?? `item-${df.entity_id}`}
								isDragDisabled={isView}
							>
								{(dragProvided, snapshot) => (
									<div
										ref={dragProvided.innerRef}
										{...dragProvided.draggableProps}
										{...dragProvided.dragHandleProps}
										style={{
											...dragProvided.draggableProps.style,
											opacity: snapshot.isDragging ? 0.7 : 1,
										}}
									>
										{getDashboardFilter(df)}
									</div>
								)}
							</Draggable>
						))}
						{provided.placeholder}
					</div>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export default DashboardFiltersHorizontalGrid;
